import { useEffect, useRef } from 'react';
import * as React from 'react';
import { observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { store } from 'Models/Store';
import { convertRefundToCredit } from 'Services/Api/_HumanWritten/GiftCertificateService';
import { formatPriceDisplay } from 'Util/_HumanWritten/PriceFormattingUtils';
import alertToast from 'Util/ToastifyUtils';
import { stringNotEmpty } from 'Util/TypeGuards';
import { MessageBox } from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { newConfirmModal } from 'Views/Components/Modal/NewConfirmModal';
import { TextArea } from 'Views/Components/TextArea/TextArea';
import { RefundData, RefundNoteModel } from './ManualRefundModal';

type ConvertRefundToCreditModalProps = {
	refund: RefundData;
	refresh?: () => void;
	onClose: () => void;
};

// A note is always required when converting, regardless of the tenant's manual-refund-note config,
// so there is always a record of why the customer was given credit instead of their money back.
const validateNote = (noteModel: RefundNoteModel): boolean => {
	let valid = true;

	runInAction(() => {
		if (!noteModel.note || noteModel.note.trim().length === 0) {
			noteModel.noteError = 'Note is required';
			valid = false;
			return;
		}

		if (noteModel.note.trim().length > 500) {
			noteModel.noteError = 'Note must be less than 500 characters';
			valid = false;
			return;
		}

		noteModel.noteError = '';
	});

	return valid;
};

const ConvertRefundToCreditModalContent = observer(
	({ refund, noteModel }: { refund: RefundData; noteModel: RefundNoteModel }) => {
		return (
			<div className="manual-refund-modal-content">
				<MessageBox status="warning" slim>
					{'This cancels the refund and gives the customer credit instead. It cannot be undone.'}
				</MessageBox>
				<p>
					Convert the pending refund of
					<strong> {formatPriceDisplay(refund.amount, false, true)}</strong> into a credit voucher? The
					refund will no longer be paid out, the credit will be added to the customer&apos;s account,
					and they will be emailed the details.
				</p>

				<div className="form-group">
					<TextArea
						id="convert-refund-credit-note"
						label="Conversion Note"
						model={noteModel}
						modelProperty="note"
						isRequired
						placeholder="Please provide the reason this refund is being converted to credit"
						onAfterChange={e => {
							runInAction(() => {
								noteModel.note = e.target.value;
								if (noteModel.noteError) {
									noteModel.noteError = '';
								}
							});
						}}
					/>
					<div className="character-count">{noteModel.note.length}/500</div>
					{noteModel.noteError && <div className="invalid-feedback">{noteModel.noteError}</div>}
				</div>
			</div>
		);
	},
);

export const ConvertRefundToCreditModal: React.FC<ConvertRefundToCreditModalProps> = observer(
	({ refund, refresh, onClose }) => {
		const modalShownRef = useRef(false);

		const showLoadingModal = (text: string, disableButtons = true) => {
			return newConfirmModal(
				'Converting Refund To Credit',
				<div className="loader-container">
					<div className="spinner-dots">
						<div className="dot" />
						<div className="dot" />
						<div className="dot" />
					</div>
					<div className="loading-text dots">{text}</div>
				</div>,
				{
					cancelText: 'Back',
					confirmText: 'Confirm',
					actionClassName: 'manual-refund-modal',
					resolveOnCancel: false,
					disableConfirmButton: disableButtons,
					disableCancelButton: disableButtons,
				},
			);
		};

		useEffect(() => {
			if (!modalShownRef.current) {
				modalShownRef.current = true;

				const noteModel = observable({
					note: '',
					noteError: '',
				});

				newConfirmModal(
					'Convert refund to credit',
					<ConvertRefundToCreditModalContent refund={refund} noteModel={noteModel} />,
					{
						cancelText: 'Back',
						confirmText: 'Convert to credit',
						actionClassName: 'manual-refund-modal',
						resolveOnCancel: false,
						onBeforeConfirm: async () => {
							const isValid = validateNote(noteModel);
							if (!isValid) {
								return false;
							}

							try {
								showLoadingModal('Converting refund to credit...');

								await convertRefundToCredit(refund.id, noteModel.note.trim());

								store.modal.hide();

								alertToast('Refund converted to a credit voucher', 'success');

								if (refresh) {
									refresh();
								}

								onClose?.();

								return true;
							} catch (error: any) {
								store.modal.hide();

								if (stringNotEmpty(error?.response?.data)) {
									alertToast(`Error converting refund: ${error?.response?.data}`, 'error');
									if (refresh) {
										refresh();
									}
								} else {
									alertToast('An error occurred while converting the refund to credit', 'error');
								}

								onClose?.();

								return false;
							}
						},
					},
				).catch(() => {
					onClose?.();
				});
			}
		}, [refund, refresh, onClose]);

		return null;
	},
);
